import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const Box = styled.div`
  position: relative;

  > .markdown {
    margin: 3rem 0 0 0;
    > p {
      font-size: 2.2rem;
      font-weight: 600;
    }
  }

  video,
  canvas {
    margin: 2rem auto;
    width: 100%;
    max-width: 400px;
    aspect-ratio: 1 / 1;
    border-radius: 12px;
    object-fit: cover;
    display: block;
  }

  .countdown {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 4rem;
    font-weight: 700;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 1rem 2rem;
    border-radius: 1rem;
    pointer-events: none;
  }

  .actions {
    justify-content: center;
    margin-top: 2rem;
    button {
      margin-right: 1rem;
    }
  }
`;

const beep = (frequency = 880, duration = 150, volume = 1) => {
  const context = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = context.createOscillator();
  const gainNode = context.createGain();

  oscillator.frequency.value = frequency;
  oscillator.type = 'sine';

  gainNode.gain.value = volume;

  oscillator.connect(gainNode);
  gainNode.connect(context.destination);

  oscillator.start();

  setTimeout(() => {
    oscillator.stop();
    context.close();
  }, duration);
};

const QuestionRecord = ({ question, timeout, stopped, onAnswered }) => {
  const previewRef = useRef(null);
  const audioRef = useRef(null);

  const [status, setStatus] = useState('idle'); // idle | countdown | recording | ready
  const [countdown, setCountdown] = useState(5);

  const chunksRef = useRef([]);
  const recorderRef = useRef(null);
  const videoStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoStreamRef.current = videoStream;
      previewRef.current.srcObject = videoStream;

      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = micStream;
      const micSource = audioContext.createMediaStreamSource(micStream);

      const audioEl = new Audio(question.recordAudio);
      audioEl.crossOrigin = 'anonymous';
      audioRef.current = audioEl;

      const fileSource = audioContext.createMediaElementSource(audioEl);
      const dest = audioContext.createMediaStreamDestination();

      micSource.connect(dest);
      fileSource.connect(dest);
      fileSource.connect(audioContext.destination);

      const combinedStream = new MediaStream([
        ...videoStream.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ]);

      const recorder = new MediaRecorder(combinedStream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = e => console.error('Recorder error:', e);
      recorder.start();

      setStatus('recording');

      audioEl.play();
      audioEl.onended = () => setStatus('ready');

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        onAnswered({ recordVideo: blob });
        cleanupMedia();
      };
    } catch (error) {
      console.error('Error starting recording:', error);
      cleanupMedia();
    }
  }, [question.recordAudio, onAnswered]);

  const cleanupMedia = () => {
    [videoStreamRef, micStreamRef].forEach(ref => {
      if (ref.current) {
        ref.current.getTracks().forEach(track => track.stop());
        ref.current = null;
      }
    });

    if (previewRef.current?.srcObject) {
      previewRef.current.srcObject.getTracks().forEach(track => track.stop());
      previewRef.current.srcObject = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }

    recorderRef.current = null;
    chunksRef.current = [];
  };

  const handleStop = () => {
    if (status !== 'ready' || timeout || stopped) return;
    const recorder = recorderRef.current;
    if (recorder) {
      const videoEl = previewRef.current;
      const canvasEl = canvasRef.current;
      if (videoEl && canvasEl) {
        const ctx = canvasEl.getContext('2d');
        canvasEl.width = videoEl.videoWidth;
        canvasEl.height = videoEl.videoHeight;
        ctx.drawImage(videoEl, 0, 0, canvasEl.width, canvasEl.height);
      }
      recorder.stop();
    }

    setStatus('idle');
  };

  const handleRepeat = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  };

  useEffect(() => {
    if (timeout || stopped) {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      } else if (chunksRef.current.length > 0) {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        onAnswered({ recordVideo: blob });
        cleanupMedia();
      } else {
        cleanupMedia();
      }
      setStatus('idle');
    }
  }, [timeout, stopped, onAnswered]);

  useEffect(() => {
    if (status === 'countdown' && countdown > 0) {
      beep(880, 150, 0.5);
    }
    if (status === 'countdown' && countdown === 0) {
      beep(440, 400, 0.5);
    }
  }, [countdown, status]);

  useEffect(() => {
    if (timeout || stopped) return;

    setStatus('countdown');
    setCountdown(5);

    const video = previewRef.current;
    const audioContext = audioContextRef.current;

    let countdownInterval;
    let startTimeout;

    countdownInterval = setInterval(() => {
      setCountdown(prev => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(countdownInterval);
          return 0;
        }
        return next;
      });
    }, 1000);

    startTimeout = setTimeout(() => {
      startRecording();
    }, 5000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(startTimeout);

      [videoStreamRef, micStreamRef].forEach(ref => {
        if (ref.current) {
          ref.current.getTracks().forEach(track => track.stop());
          ref.current = null;
        }
      });

      if (video?.srcObject) {
        video.srcObject.getTracks().forEach(track => track.stop());
        video.srcObject = null;
      }

      if (audioContext) {
        audioContext.close();
        audioContextRef.current = null;
      }
    };
  }, [question, startRecording, timeout, stopped]);

  const isCountdown = status === 'countdown';
  const isIdle = status === 'idle';
  const isRecording = status === 'recording';
  const isReady = status === 'ready';

  return (
    <Box $question={question} $timeout={timeout} $stopped={stopped}>
      <video ref={previewRef} autoPlay playsInline muted style={{ display: (isRecording || isReady) ? 'block' : 'none' }} />
      <canvas ref={canvasRef} style={{ display: (isIdle || isCountdown) ? 'block' : 'none' }} />

      {isCountdown && ( <div className="countdown">{countdown}</div> )}

      <div className="actions">
        <button onClick={handleRepeat} disabled={!isReady || timeout || stopped}>Repeat Question</button>
        <button className="primary" onClick={handleStop} disabled={!isReady || timeout || stopped}>Send Answer</button>
      </div>
    </Box>
  );
};

export default QuestionRecord;
